import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { BadgeCheck, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from './orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CertificateCard = ({ order, onSubmitted }) => {
  const { user } = useAuth();
  const [cert, setCert] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    device_type: order.product_label || '', fabrication_date: '',
    country_of_manufacture: '', operator_name: '', signed_by: user?.name || '',
    conformity_declaration: false,
  });
  const [materials, setMaterials] = useState([{ name: '', lot_number: '' }]);

  const fetchCert = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${order.id}/certificate`);
      setCert(data);
    } catch (e) {
      setCert(null);
    } finally {
      setLoaded(true);
    }
  }, [order.id]);

  useEffect(() => { fetchCert(); }, [fetchCert]);

  const submit = async () => {
    if (!form.conformity_declaration) { toast.error('Confirmez la déclaration de conformité'); return; }
    const mats = materials.filter((m) => m.name.trim());
    if (!mats.length || !form.device_type || !form.fabrication_date || !form.country_of_manufacture || !form.operator_name) {
      toast.error('Tous les champs du certificat sont requis'); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/orders/${order.id}/certificate`, { ...form, materials: mats });
      toast.success('Certificat de conformité généré et archivé au dossier');
      setShowForm(false);
      fetchCert();
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur certificat');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  if (cert) {
    return (
      <Card className="border-green-200 bg-green-50/40" data-testid="certificate-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-green-600" />Certificat de conformité</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <p><span className="text-muted-foreground">Signé par :</span> {cert.signed_by} · {fmtDateTime(cert.signed_at)}</p>
          <p><span className="text-muted-foreground">Empreinte :</span> <span className="font-mono text-xs">{cert.signature_hash?.slice(0, 24)}…</span></p>
          <p className="text-muted-foreground text-xs">Le PDF standardisé est disponible dans les fichiers du dossier.</p>
        </CardContent>
      </Card>
    );
  }

  if (user?.role !== 'lab') {
    return (
      <Card className="border-amber-200 bg-amber-50/40" data-testid="certificate-missing-card">
        <CardContent className="py-4 text-sm text-amber-800">
          Certificat de conformité non déposé — le dossier ne pourra pas être terminé sans lui.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200" data-testid="certificate-form-card">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Certificat de conformité (obligatoire)</CardTitle>
        {!showForm && <Button size="sm" onClick={() => setShowForm(true)} data-testid="certificate-open-form-btn">Remplir le certificat</Button>}
      </CardHeader>
      {showForm && (
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Nature du dispositif</Label>
              <Input value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })} data-testid="cert-device-input" />
            </div>
            <div>
              <Label>Date de fabrication</Label>
              <Input type="date" value={form.fabrication_date} onChange={(e) => setForm({ ...form, fabrication_date: e.target.value })} data-testid="cert-date-input" />
            </div>
            <div>
              <Label>Pays de fabrication</Label>
              <Input value={form.country_of_manufacture} onChange={(e) => setForm({ ...form, country_of_manufacture: e.target.value })} placeholder="Chine" data-testid="cert-country-input" />
            </div>
            <div>
              <Label>Opérateur</Label>
              <Input value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} data-testid="cert-operator-input" />
            </div>
          </div>
          <div>
            <Label>Matériaux et lots</Label>
            {materials.map((m, i) => (
              <div key={i} className="flex gap-2 mt-1">
                <Input placeholder="Matériau (ex: Zircone 3Y-TZP)" value={m.name}
                  onChange={(e) => setMaterials(materials.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  data-testid={`cert-material-${i}`} />
                <Input placeholder="N° de lot" className="w-40" value={m.lot_number}
                  onChange={(e) => setMaterials(materials.map((x, j) => j === i ? { ...x, lot_number: e.target.value } : x))} />
                <Button variant="ghost" size="icon" onClick={() => setMaterials(materials.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2 gap-1" onClick={() => setMaterials([...materials, { name: '', lot_number: '' }])}>
              <Plus className="h-3 w-3" />Ajouter un matériau
            </Button>
          </div>
          <div>
            <Label>Signataire</Label>
            <Input value={form.signed_by} onChange={(e) => setForm({ ...form, signed_by: e.target.value })} data-testid="cert-signedby-input" />
          </div>
          <div className="flex items-start gap-2 rounded-md border border-border p-3">
            <Checkbox id="conf" checked={form.conformity_declaration}
              onCheckedChange={(v) => setForm({ ...form, conformity_declaration: !!v })} data-testid="cert-declaration-checkbox" />
            <Label htmlFor="conf" className="text-xs leading-relaxed cursor-pointer">
              Je déclare que ce dispositif médical sur mesure est conforme aux exigences applicables
              (Règlement UE 2017/745, annexe I) et fabriqué selon la prescription du praticien.
            </Label>
          </div>
          <Button onClick={submit} disabled={busy} className="gap-2" data-testid="cert-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Signer et générer le certificat
          </Button>
        </CardContent>
      )}
    </Card>
  );
};
